import { isOrgIdLike } from '../url/orgPath'

/**
 * What to do about the org in the URL's path (`/orgs/<orgId>/…`) — pure, so
 * every branch is testable. `OrgPathGuard` acts on the answer.
 *
 * The path is a REQUEST, never an instruction: a URL without it opens in the
 * reader's current org exactly as URLs always did (and gains the prefix), and
 * one with it switches only into an org the reader belongs to, through the
 * same token exchange the org picker uses (`switchToOrg`).
 */

export type MembershipState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; ids: readonly string[] }

export type PathOrgDenial = 'not-member' | 'obo' | 'failed'

export type PathOrgDecision =
  | { kind: 'none' }
  /** An old URL without the prefix: add the current org to it. */
  | { kind: 'canonicalize'; orgId: string }
  | { kind: 'wait' }
  | { kind: 'switch'; orgId: string }
  | { kind: 'deny'; reason: PathOrgDenial }

export function decidePathOrg({
  pathOrg,
  currentOrgId,
  isOBO,
  members,
  attempted,
}: {
  pathOrg: string | null
  currentOrgId: string | undefined
  isOBO: boolean
  members: MembershipState
  /** The org this browser tab last tried to switch into, if any. */
  attempted: string | null
}): PathOrgDecision {
  // Every URL from before the prefix existed: the session's org is the one it
  // always meant. An account-level on-behalf-of session has no org to add.
  if (!pathOrg) {
    return currentOrgId ? { kind: 'canonicalize', orgId: currentOrgId } : { kind: 'none' }
  }
  if (pathOrg === currentOrgId) return { kind: 'none' }

  // Not an org id at all (a page name, a sentence): nothing to switch to, and
  // nothing to send the token exchange.
  if (!isOrgIdLike(pathOrg)) return { kind: 'deny', reason: 'not-member' }

  // An on-behalf-of session is a staff login lent to one org in one tab.
  // Switching would write tokens over the staff member's own login, and the
  // org picker is hidden in OBO for the same reason. Checked before the wait
  // below: an ACCOUNT-level OBO session can have no current org at all, and
  // waiting for one would hold the page forever.
  if (isOBO) return { kind: 'deny', reason: 'obo' }
  if (!currentOrgId) return { kind: 'wait' }

  // Tried already and still not there: the exchange failed, "succeeded" into
  // some other org, or the reload lost the tokens. Trying again by itself
  // would reload forever; the access page offers a deliberate retry.
  if (attempted === pathOrg) return { kind: 'deny', reason: 'failed' }

  if (members.status === 'loading') return { kind: 'wait' }
  // The org list is a pre-check, not the authority: if it could not load, the
  // exchange itself says whether the reader is allowed in.
  if (members.status === 'ok' && !members.ids.includes(pathOrg)) {
    return { kind: 'deny', reason: 'not-member' }
  }
  return { kind: 'switch', orgId: pathOrg }
}
